import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Row,
    Col,
    Label,
    FormGroup,
    FormFeedback,
    Spinner,
} from "reactstrap";
import { AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import { submitUserWarningLetterFilterData } from "model/UserWarningLetterModel";
import {
    applyUserAsyncFilterOptionToFormState,
    buildUserAsyncFilterInitialOption,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";

const userLabelFieldKey = getUserAsyncFilterLabelFieldKey();

const defaultWarningLetterForm = {
    id: "",
    userId: "",
    [userLabelFieldKey]: "",
    warningLetterDate: "",
    warningLetterNote: "",
};

const formatDateForInput = (value) => {
    if (!value) return "";
    const stringValue = String(value);
    return stringValue.includes("T") ? stringValue.split("T")[0] : stringValue;
};

const mapFilterRecordToForm = (record = {}, fallback = {}) => ({
    id: String(record?.id ?? record?._id ?? fallback?.id ?? "").trim(),
    userId: String(record?.userId ?? record?.warningLetterUserId ?? fallback?.userId ?? "").trim(),
    [userLabelFieldKey]: String(
        record?.userName ?? record?.user ?? fallback?.userName ?? fallback?.[userLabelFieldKey] ?? ""
    ).trim(),
    warningLetterDate: formatDateForInput(
        record?.warningLetterDate ?? record?.date ?? fallback?.warningLetterDate ?? ""
    ),
    warningLetterNote: String(
        record?.warningLetterNote ?? record?.note ?? fallback?.warningLetterNote ?? ""
    ).trim(),
});

const WarningLetterAddEditModal = ({
    warningLetterAddEditModalOpen,
    setWarningLetterAddEditModalOpen,
    editableData = null,
    onSubmit,
}) => {
    const [warningLetterForm, setWarningLetterForm] = useState({ ...defaultWarningLetterForm });
    const [errors, setErrors] = useState({});
    const [loadingEdit, setLoadingEdit] = useState(false);

    useEffect(() => {
        if (!warningLetterAddEditModalOpen) return;

        let cancelled = false;

        const loadForm = async () => {
            const recordId = String(editableData?.id ?? editableData?._id ?? "").trim();

            if (!recordId) {
                setWarningLetterForm({ ...defaultWarningLetterForm });
                setErrors({});
                return;
            }

            setLoadingEdit(true);
            try {
                const response = await submitUserWarningLetterFilterData({
                    id: recordId,
                    pageNumber: 1,
                    pageSize: 1,
                });
                if (cancelled) return;

                const records = response?.data?.records ?? [];
                const record = Array.isArray(records) ? records[0] : null;

                setWarningLetterForm(
                    record
                        ? mapFilterRecordToForm(record, editableData)
                        : mapFilterRecordToForm(editableData, {})
                );
            } catch {
                if (!cancelled) {
                    setWarningLetterForm(mapFilterRecordToForm(editableData, {}));
                }
            } finally {
                if (!cancelled) {
                    setLoadingEdit(false);
                    setErrors({});
                }
            }
        };

        loadForm();

        return () => {
            cancelled = true;
        };
    }, [editableData, warningLetterAddEditModalOpen]);

    const closeModal = () => {
        setWarningLetterAddEditModalOpen(false);
    };

    const handleWarningLetterFormChange = (field, value) => {
        setWarningLetterForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const copy = { ...prev };
            delete copy[field];
            return copy;
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!String(warningLetterForm?.userId || "").trim()) {
            newErrors.userId = "User is required.";
        }
        if (!String(warningLetterForm?.warningLetterDate || "").trim()) {
            newErrors.warningLetterDate = "Date is required.";
        }
        if (!String(warningLetterForm?.warningLetterNote || "").trim()) {
            newErrors.warningLetterNote = "Note is required.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;
        const finalData = {
            id: warningLetterForm.id,
            userId: warningLetterForm.userId,
            warningLetterDate: warningLetterForm.warningLetterDate,
            warningLetterNote: warningLetterForm.warningLetterNote,
        };

        if (typeof onSubmit === "function") {
            onSubmit(finalData);
        }
    };

    return (
        <Modal
            isOpen={warningLetterAddEditModalOpen}
            toggle={closeModal}
            backdrop="static"
            size="md"
            className="modal-full-height"
            centered
        >
            <ModalBody className="modal-height-fixed overflow-y-auto">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0">
                        {warningLetterForm?.id ? "WARNING LETTER UPDATE" : "WARNING LETTER CREATE"}
                    </h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                {loadingEdit ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <Row className="mt-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="fw-semibold">
                                        User <span className="text-danger">*</span>
                                    </Label>
                                    <div className={errors.userId ? "is-invalid" : ""}>
                                        <AsyncUserSelect
                                            key={`warning-letter-modal-user-${warningLetterForm.userId}-${warningLetterForm.id}`}
                                            pageName="WarningLetter"
                                            value={warningLetterForm.userId}
                                            initialOption={buildUserAsyncFilterInitialOption(
                                                warningLetterForm.userId,
                                                warningLetterForm[userLabelFieldKey]
                                            )}
                                            onChange={(option) =>
                                                setWarningLetterForm((prev) =>
                                                    applyUserAsyncFilterOptionToFormState(prev, option)
                                                )
                                            }
                                            placeholder="Select User"
                                        />
                                    </div>
                                    {errors.userId && (
                                        <FormFeedback className="d-block">{errors.userId}</FormFeedback>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="fw-semibold">
                                        Date <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="date"
                                        value={warningLetterForm?.warningLetterDate || ""}
                                        invalid={!!errors.warningLetterDate}
                                        onChange={(e) =>
                                            handleWarningLetterFormChange(
                                                "warningLetterDate",
                                                e?.target?.value || ""
                                            )
                                        }
                                    />
                                    <FormFeedback>{errors.warningLetterDate}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="fw-semibold">
                                        Note <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        value={warningLetterForm?.warningLetterNote || ""}
                                        invalid={!!errors.warningLetterNote}
                                        onChange={(e) =>
                                            handleWarningLetterFormChange(
                                                "warningLetterNote",
                                                e?.target?.value || ""
                                            )
                                        }
                                    />
                                    <FormFeedback>{errors.warningLetterNote}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="text-end mt-4">
                            <Button color="primary" onClick={handleSubmit}>
                                {/* <i className="bx bx-save me-1"></i> */}
                                {warningLetterForm?.id ? "Update" : "Save"}
                            </Button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default WarningLetterAddEditModal;
